import React, { useEffect, useState } from "react";
import PageHelp from "@/components/PageHelp";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, Plus, Loader2, KeyRound, ShieldCheck, User as UserIcon, Trash2, FolderKanban, SlidersHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WORKSPACE_ROLE_OPTIONS } from "@/lib/inspectionConstants";
import RolesManager from "@/components/RolesManager";
import ProjectTeams from "@/components/ProjectTeams";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { email: "", password: "", name: "", role: "user", allowed_projects: [] };

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [delFor, setDelFor] = useState(null);
  const [projFor, setProjFor] = useState(null);
  const [selProj, setSelProj] = useState([]);
  const [savingProj, setSavingProj] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [roleOpts, setRoleOpts] = useState(WORKSPACE_ROLE_OPTIONS);
  const loadRoles = () => api.get("/roles").then((r) => setRoleOpts(r.data.roles.map((x) => ({ value: x.key, label: x.label })))).catch(() => {});

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); loadProjects(); loadRoles(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Benutzer angelegt – Einladung per E-Mail gesendet");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setRole = async (u, role) => {
    try { await api.put(`/admin/users/${u.user_id}`, { role }); toast.success("Rolle aktualisiert"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setActive = async (u, active) => {
    try { await api.put(`/admin/users/${u.user_id}`, { active }); toast.success(active ? "Konto aktiviert" : "Konto deaktiviert"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Passwort muss mind. 6 Zeichen haben");
    try { await api.post(`/admin/users/${pwFor.user_id}/reset-password`, { password: newPw }); toast.success("Passwort zurückgesetzt"); setPwFor(null); setNewPw(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeUser = async () => {
    try { await api.delete(`/admin/users/${delFor.user_id}`); toast.success("Benutzer gelöscht"); setDelFor(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openProjects = (u) => { setProjFor(u); setSelProj(u.allowed_projects || []); };
  const toggleSel = (id) => setSelProj((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const saveProjects = async () => {
    setSavingProj(true);
    try {
      await api.put(`/admin/users/${projFor.user_id}`, { allowed_projects: selProj });
      toast.success(selProj.length ? `${selProj.length} Projekt(e) zugewiesen` : "Alle Projekte sichtbar");
      setProjFor(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingProj(false); }
  };

  const toggleCreateProj = (id) => setForm((f) => ({ ...f, allowed_projects: f.allowed_projects.includes(id) ? f.allowed_projects.filter((x) => x !== id) : [...f.allowed_projects, id] }));

  return (
    <div className="space-y-6 fade-up" data-testid="user-management-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Users className="h-8 w-8 text-primary" />Benutzerverwaltung <PageHelp id="benutzer" title="Benutzerverwaltung" text="Laden Sie Team-Mitglieder ein, vergeben Sie Rollen, weisen Sie einzelne Projekte zu (Haustechnik/Leitung sehen nur zugewiesene Projekte) und steuern Sie Konten." /></h1>
          <p className="text-slate-500 mt-1">Rollen vergeben, Projekte zuweisen, Passwörter zurücksetzen und Konten steuern.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-11" onClick={() => setMatrixOpen(true)} data-testid="perm-matrix-btn"><SlidersHorizontal className="h-4 w-4 mr-2" />Rollen & Rechte</Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(EMPTY); }}>
            <DialogTrigger asChild>
              <Button className="h-11" data-testid="new-user-btn"><Plus className="h-4 w-4 mr-2" />Neuer Benutzer</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-head text-xl">Neuen Benutzer anlegen</DialogTitle><DialogDescription>Rolle und optionalen Projektzugriff festlegen – der Benutzer erhält eine Einladung per E-Mail.</DialogDescription></DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" /></div>
                <div><Label>E-Mail *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" /></div>
                <div><Label>Startpasswort *</Label><Input type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="user-password-input" placeholder="mind. 6 Zeichen" /></div>
                <p className="text-xs text-slate-500">Der Benutzer erhält eine Einladungs-E-Mail, um sein eigenes Passwort festzulegen.</p>
                <div><Label>Rolle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roleOpts.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.role !== "admin" && projects.length > 0 && (
                  <div>
                    <Label>Projektzugriff</Label>
                    <p className="text-[11px] text-slate-400 mb-1.5">Keine Auswahl = alle Projekte sichtbar. Auswahl = nur diese Projekte.</p>
                    <ScrollArea className="h-32 rounded-lg border border-slate-200 p-2">
                      {projects.map((p) => (
                        <label key={p.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`create-proj-${p.id}`}>
                          <Checkbox checked={form.allowed_projects.includes(p.id)} onCheckedChange={() => toggleCreateProj(p.id)} />
                          <span className="truncate">{p.name}</span>
                        </label>
                      ))}
                    </ScrollArea>
                  </div>
                )}
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="w-full" data-testid="save-user-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Benutzer anlegen"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="divide-y divide-slate-100">
        {!users ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-slate-400">Noch keine Benutzer.</div>
        ) : users.map((u) => {
          const isMe = u.user_id === me?.user_id;
          const active = u.active !== false;
          const isAdminRole = u.role === "admin";
          const assigned = u.allowed_projects || [];
          return (
            <div key={u.user_id} className="flex items-center gap-3 p-4 flex-wrap" data-testid={`user-row-${u.user_id}`}>
              <div className="h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                {(u.name || u.email).slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{u.name || u.email} {isMe && <span className="text-xs text-slate-400">(Sie)</span>}</div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
              </div>
              {!active && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Deaktiviert</Badge>}
              <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={isMe}>
                <SelectTrigger className="w-44 h-9" data-testid={`role-select-${u.user_id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOpts.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      <span className="flex items-center gap-1.5">{r.value === "admin" ? <ShieldCheck className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}{r.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => openProjects(u)} disabled={isAdminRole} data-testid={`projects-btn-${u.user_id}`} title={isAdminRole ? "Administratoren sehen alle Projekte" : "Projektzugriff festlegen"}>
                <FolderKanban className="h-3.5 w-3.5 mr-1" />{isAdminRole ? "Alle Projekte" : (assigned.length ? `${assigned.length} Projekt${assigned.length === 1 ? "" : "e"}` : "Alle Projekte")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setPwFor(u); setNewPw(""); }} data-testid={`reset-pw-btn-${u.user_id}`}>
                <KeyRound className="h-3.5 w-3.5 mr-1" />Passwort
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Aktiv</span>
                <Switch checked={active} onCheckedChange={(v) => setActive(u, v)} disabled={isMe} data-testid={`active-switch-${u.user_id}`} />
              </div>
              {!isMe && (
                <button onClick={() => setDelFor(u)} data-testid={`delete-user-${u.user_id}`} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500" title="Benutzer löschen"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          );
        })}
      </Card>

      <ProjectTeams users={users} projects={projects} roleOpts={roleOpts} />

      {/* Projektzugriff zuweisen */}
      <Dialog open={!!projFor} onOpenChange={(o) => !o && setProjFor(null)}>
        <DialogContent className="max-w-md" data-testid="assign-projects-dialog">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><FolderKanban className="h-5 w-5 text-primary" />Projektzugriff · {projFor?.name || projFor?.email}</DialogTitle><DialogDescription>Legen Sie fest, welche Projekte dieser Benutzer sehen darf.</DialogDescription></DialogHeader>
          <p className="text-xs text-slate-500">Keine Auswahl = <b>alle</b> Projekte sichtbar. Auswahl = der Benutzer sieht <b>nur</b> diese Projekte (überall: Liste, Baupläne, Mängel, Tickets, Dokumente, Suche).</p>
          {projects.length === 0 ? (
            <p className="py-8 text-center text-slate-400 text-sm">Noch keine Projekte angelegt.</p>
          ) : (
            <ScrollArea className="h-64 rounded-lg border border-slate-200 p-2 mt-1">
              {projects.map((p) => (
                <label key={p.id} className="flex items-center gap-2.5 py-1.5 text-sm cursor-pointer" data-testid={`assign-proj-${p.id}`}>
                  <Checkbox checked={selProj.includes(p.id)} onCheckedChange={() => toggleSel(p.id)} />
                  <span className="truncate flex-1">{p.name}</span>
                  {p.address && <span className="text-xs text-slate-400 truncate max-w-[40%]">{p.address}</span>}
                </label>
              ))}
            </ScrollArea>
          )}
          <div className="flex items-center justify-between gap-2">
            <button type="button" className="text-xs text-slate-500 hover:text-primary" onClick={() => setSelProj([])} data-testid="assign-clear-btn">Alle sichtbar (Auswahl aufheben)</button>
            <span className="text-xs text-slate-400">{selProj.length ? `${selProj.length} ausgewählt` : "alle sichtbar"}</span>
          </div>
          <DialogFooter>
            <Button onClick={saveProjects} disabled={savingProj} className="w-full" data-testid="save-projects-btn">{savingProj ? <Loader2 className="h-4 w-4 animate-spin" /> : "Zugriff speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RolesManager open={matrixOpen} onOpenChange={setMatrixOpen} onRolesChanged={() => loadRoles()} />

      <Dialog open={!!pwFor} onOpenChange={(o) => { if (!o) { setPwFor(null); setNewPw(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Passwort zurücksetzen</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Neues Passwort für <span className="font-medium text-slate-700">{pwFor?.email}</span>. Bestehende Sitzungen werden abgemeldet.</p>
          <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Neues Passwort (mind. 6 Zeichen)" className="mt-2" data-testid="new-password-input" />
          <DialogFooter>
            <Button onClick={resetPw} className="w-full" data-testid="confirm-reset-pw-btn">Passwort setzen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delFor} onOpenChange={(o) => !o && setDelFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
            <AlertDialogDescription>Das Konto von <b>{delFor?.name || delFor?.email}</b> wird endgültig entfernt. Bereits erfasste Inhalte bleiben im Arbeitsbereich erhalten.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={removeUser} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user-btn">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
